import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "store";

import {
  deleteSeo,
  getSeoList,
  handlePaginate,
  SeoItem,
} from "store/cms/pages/seo/slice";
import { bonusesListLimits, cmsSeoListLimits } from "configs/limits";

import Pagination from "components/Pagination";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import SeoFilter from "./components/SeoFilter";
import ActionBtn from "components/buttons/ActionBtn";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";

const SeoList: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [currentLimit, setLimit] = useState<number>(cmsSeoListLimits[0]);

  const list = useAppSelector((store) => store.cmsSeo.list);
  const isListFetched = useAppSelector((store) => store.cmsSeo.isListFetched);
  const totalItems = useAppSelector((store) => store.cmsSeo.totalItems);
  const totalPages = useAppSelector((store) => store.cmsSeo.totalPages);
  const activePage = useAppSelector((store) => store.cmsSeo.activePage);

  const [filters, setFilters] = useState<{
    search: string;
    active?: number;
    types?: string[];
  }>({ search: "" });

  const fetchList = () => {
    dispatch(
      getSeoList({
        limit: currentLimit,
        offset: activePage - 1,
        title: filters.search,
        isActive: filters.active,
        types: filters.types,
      }),
    );
  };

  const processingItemId = useAppSelector(
    (store) => store.cmsSeo.processingItemId,
  );

  const permissions = usePermissionHelper();
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'cmsSeo',
    isListFetched
  });

  useEffect(() => {
     if (!isListFetched) {
    fetchList();
  }
  }, [activePage, currentLimit, isListFetched]);

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleApplyFilter = (params: {
    search: string;
    active?: number;
    type?: string[];
  }) => {
    setFilters({
      search: params.search,
      active: params.active,
      types: params.type,
    });

    dispatch(handlePaginate(1));
  };

  const handleDeleteItem = async (id: number) => {
    const result = await dispatch(deleteSeo(id));

    dispatch(
      showNotification({
        title: result?.payload ? t("success") : t("error"),
        message: result?.payload
          ? t("entityDeleteSuccess")
          : t("entityDeleteError"),
        type: result?.payload
          ? NotificationTypes.success
          : NotificationTypes.error,
      }),
    );
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.seo}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.seo }));
  };

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleOpenEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleOpenEditModal(id),
    };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <SeoFilter onApply={handleApplyFilter} />

      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {permissions?.create && (
              <ActionBtn
                txt={t("addSeo")}
                handleClick={() => {
                  navigate(`${location.pathname}?activeModal=${ModalTypes.seo}`);
                  dispatch(openModal({ activeModal: ModalTypes.seo }));
                }}
              />
            )}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={4} rowsCount={4} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <div className="game-table-wrap">
            {list.length === 0 ? (
              <EmptyState message={t("noSeoDataFound")} />
            ) : (
              <Table
                data={list as Array<SeoItem & { id: number }>}
                idField="id"
                onDelete={handleDeleteItem}
                hasCheckbox={false}
                processingId={processingItemId}
                hasDelete={permissions?.delete}
                hasEdit={permissions?.update}
                hasView={permissions?.read && !permissions?.update}
                hasOptions={permissions?.read || permissions?.update}
                {...actionProps}
              />
            )}
          </div>
        )}

        {isListFetched && list.length > 0 && (
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              dispatch(handlePaginate(1));
            }}
            limitsArray={bonusesListLimits}
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        )}
      </div>
    </div>
  );
};

export default SeoList;
